'use client'

import { twMerge } from '@/lib/classname/twMerge'
import type { ReactNode } from 'react'
import {
  TagGroup as AriaTagGroup,
  type TagGroupProps as AriaTagGroupProps,
  TagList,
} from 'react-aria-components'
import { Tag } from './Tag.client'

type StaticTags = {
  children?: ReactNode
  items?: never
}

type DynamicTags<T extends object> = {
  children?: (item: T) => ReactNode
  items?: Iterable<T>
}

type SelectableTags = StrictPick<
  AriaTagGroupProps,
  'selectionMode' | 'onSelectionChange' | 'selectedKeys' | 'defaultSelectedKeys' | 'disabledKeys'
> & {
  onRemove?: never
}

type DeletableTags = StrictPick<AriaTagGroupProps, 'onRemove'> & {
  onSelectionChange?: never
  selectionMode?: never
  selectedKeys?: never
  defaultSelectedKeys?: never
  disabledKeys?: never
}

export type TagGroupProps<T extends object> = UiComponentProps & {
  size: 'md' | 'sm'
} & (SelectableTags | DeletableTags) &
  (StaticTags | DynamicTags<T>)

function TagGroup<T extends object>({
  id,
  size,
  selectionMode,
  onSelectionChange,
  selectedKeys,
  defaultSelectedKeys,
  onRemove,
  disabledKeys,
  items,
  children,
  className,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: TagGroupProps<T>) {
  return (
    <AriaTagGroup
      id={id}
      selectionMode={selectionMode}
      onSelectionChange={onSelectionChange}
      selectedKeys={selectedKeys}
      defaultSelectedKeys={defaultSelectedKeys}
      onRemove={onRemove}
      shouldSelectOnPressUp
      aria-label={ariaLabel ?? 'Tag group'}
      data-testid={dataTestId}
      disabledKeys={disabledKeys}
    >
      <TagList
        items={items}
        className={twMerge('group flex flex-wrap gap-1', className)}
        data-size={size}
      >
        {children}
      </TagList>
    </AriaTagGroup>
  )
}

TagGroup.Item = Tag

export { TagGroup }
